import * as React from "react";
import useApi from "@/hooks/useApi";

export interface KBStatus {
  ok: boolean;
  stage?: string;
  error?: string;
  siteId?: number;
  server?: string;
  database?: string;
  isAlternate?: boolean;
  isVelaroAdmin?: boolean;
  linkedTenantId?: string;
  companyName?: string;
  domain?: string;
  articleCount?: number;
}

/**
 * Checks whether this site has a KB tenant linked and the KB is reachable.
 * Returns `status` with full diagnostic info so the UI can show a helpful error.
 */
export default function useKBConnection() {
  const [connected, setConnected] = React.useState<boolean | null>(null);
  const [kbStatus, setKbStatus] = React.useState<KBStatus | null>(null);
  const api = useApi();

  React.useEffect(() => {
    async function check() {
      try {
        const response = await api.admin.get("api/kb/status");
        const status: KBStatus = await response.json();
        setKbStatus(status);
        setConnected(status.ok === true);
      } catch {
        // Network error — show the page, articles fetch will also fail
        setConnected(false);
        setKbStatus({ ok: false, stage: "network", error: "Could not reach the KB API. Check your network." });
      }
    }
    check();
  }, []);

  async function forceConnect(): Promise<{ success: boolean; message: string }> {
    try {
      const r = await api.admin.post("api/kb/status/force-connect", {});
      const json = await r.json();
      if (json.success) {
        // Re-check status after connecting
        const r2 = await api.admin.get("api/kb/status");
        const status2: KBStatus = await r2.json();
        setKbStatus(status2);
        setConnected(status2.ok === true);
      }
      return { success: json.success === true, message: json.message ?? json.error ?? "Unknown result" };
    } catch (ex) {
      return { success: false, message: String(ex) };
    }
  }

  return { connected, kbStatus, forceConnect };
}
